import { Box, Grid, IconButton, Typography, useMediaQuery } from '@mui/material'
import { Fragment, useState } from 'react'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { Form, Formik, useFormikContext } from 'formik'
import { useAlert } from 'providers/AlertProvider'
import Input from 'components/Input/Input'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { Autocomplete } from 'components/Autocomplete/Autocomplete'
import { useCodeList } from 'hooks/useCodeList'
import { t } from 'i18next'
import theme from 'lib/theme'
import { PackagingMaterialType } from 'types/api/product'
import { usePutMasterProduct } from 'hooks/useMasterProducts'
import { useProductInfo } from './Product'

type FormikFieldType = {
  width: string
  widthUnit: { label: string; value: string }
  height: string
  heightUnit: { label: string; value: string }
  depth: string
  depthUnit: { label: string; value: string }
  grossWeight: string
  grossWeightUnit: { label: string; value: string }
  packagingTypeCode: { label: string; value: string }
  packagingMaterial: {
    packagingMaterialTypeCode: string
    isPackagingMaterialRecoverable: boolean
  }[]
}

const PackagingMaterial = ({
  materials,
}: {
  materials: PackagingMaterialType[]
}) => {
  const { setFieldValue } = useFormikContext()
  const { codeListTypes: materialTypeCodes } = useCodeList(
    'packagingMaterialTypeCode',
    true
  )
  const [code, setCode] = useState<{ value: string; label: string } | null>(
    null
  )
  const [recoverable, setRecoverable] = useState<{
    value: string
    label: string
  } | null>(null)
  const recoverableOptions = [
    { value: 'true', label: t('recoverable') },
    { value: 'false', label: t('notRecoverable') },
  ]

  const handleDelete = (material: PackagingMaterialType) => {
    const newMaterials = materials.filter(
      (tempMaterial) =>
        tempMaterial.packagingMaterialTypeCode !==
        material.packagingMaterialTypeCode
    )
    setFieldValue('packagingMaterial', [...newMaterials])
  }

  const handleAdd = () => {
    if (code && recoverable) {
      const newMaterial = {
        packagingMaterialTypeCode: code.value,
        isPackagingMaterialRecoverable: Boolean(recoverable.value === 'true'),
      }
      const newMaterials = [...materials]
      newMaterials.push(newMaterial)
      setFieldValue('packagingMaterial', newMaterials)
      setCode(null)
      setRecoverable(null)
    }
  }

  return (
    <>
      <Typography variant='body2'>{t('packagingMaterial')}</Typography>
      <Grid container spacing={2} mt={0} sx={{ alignItems: 'center' }}>
        {materials?.length > 0 &&
          materials.map((material) => {
            const packagingCode = materialTypeCodes.find(
              (code: { label: string; value: string }) =>
                code.value === material.packagingMaterialTypeCode
            )
            const isRecoverable =
              material.isPackagingMaterialRecoverable === true
                ? t('recoverable')
                : t('notRecoverable')
            return (
              <Fragment key={material.packagingMaterialTypeCode}>
                <Grid item xs={7}>
                  <Typography
                    sx={{ fontSize: '18px', paddingLeft: theme.spacing(1) }}>
                    {packagingCode?.label || ''}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: theme.palette.text.secondary,
                      paddingLeft: theme.spacing(1),
                    }}>
                    {isRecoverable}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    aria-label={t('deleteCategory')}
                    onClick={() => handleDelete(material)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Fragment>
            )
          })}
        <Grid item xs={7}>
          <Autocomplete
            options={materialTypeCodes ?? []}
            onChange={(newUnit) => {
              setCode(newUnit)
            }}
            onClear={() => setCode(null)}
            value={code || null}
            name='material'
            label=''
            placeholder={t('material')}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectDropdown
            customPlaceholderMessage={t('recoverable')}
            marginBottom='0px'
            selectOptions={recoverableOptions ?? []}
            label=''
            selectedValue={recoverable?.value || ''}
            handleChange={(value) => {
              const newRecoverable = recoverableOptions.find(
                (option) => option.value === value
              )
              if (newRecoverable) {
                setRecoverable(newRecoverable)
              }
            }}
          />
        </Grid>
        <Grid item xs={1}>
          <IconButton
            sx={{
              '&:disabled': {
                '& .MuiSvgIcon-root': { fill: theme.palette.action.disabled },
              },
            }}
            aria-label={t('addCategory')}
            onClick={handleAdd}
            disabled={!code || recoverable === null}>
            <AddIcon />
          </IconButton>
        </Grid>
      </Grid>
    </>
  )
}

export const ProductPackagingInfo = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { masterProductId } = useParams()
  const { product, masterProduct } = useProductInfo()
  const { setAlert } = useAlert()
  const { codeListTypes: productPackagingOptions } = useCodeList(
    'packagingTypeCode',
    true
  )
  const { codeListTypes: measurementUnitOptions } = useCodeList(
    'measurementUnitCode',
    true
  )
  const { mutateAsync, isLoading: isLoadingPutRequest } = usePutMasterProduct(
    masterProductId as string
  )
  const initialValues = {
    packagingTypeCode:
      product?.packagingType && productPackagingOptions?.length > 0
        ? productPackagingOptions.find(
            (option: { value: string; label: string }) =>
              option.value === product.packagingType
          )
        : null,
    packagingMaterial: product?.packagingMaterial,
    width: product?.width ? String(product?.width) : '',
    widthUnit:
      product?.widthUnit && measurementUnitOptions?.length > 0
        ? measurementUnitOptions.find(
            (code: { value: string; label: string }) =>
              code.value === product.widthUnit
          )
        : null,
    height: product?.height ? String(product?.height) : '',
    heightUnit:
      product?.heightUnit && measurementUnitOptions?.length > 0
        ? measurementUnitOptions.find(
            (code: { value: string; label: string }) =>
              code.value === product.heightUnit
          )
        : null,
    depth: product?.depth ? String(product?.depth) : '',
    depthUnit:
      product?.depthUnit && measurementUnitOptions?.length > 0
        ? measurementUnitOptions.find(
            (code: { value: string; label: string }) =>
              code.value === product.depthUnit
          )
        : null,
    grossWeight: product?.grossWeight ? String(product?.grossWeight) : '',
    grossWeightUnit:
      product?.grossWeightUnit && measurementUnitOptions?.length > 0
        ? measurementUnitOptions.find(
            (code: { value: string; label: string }) =>
              code.value === product.grossWeightUnit
          )
        : null,
  }

  const validationSchema = yup.object().shape(
    {
      width: yup
        .number()
        .transform((_value, originalValue) =>
          Number(originalValue.replace(/,/, '.'))
        )
        .typeError(t('mustBeNumeric'))
        .when('widthUnit', {
          is: (widthUnit: { label: string; value: string } | null) =>
            widthUnit?.value,
          then: yup
            .number()
            .transform((_value, originalValue) =>
              Number(originalValue.replace(/,/, '.'))
            )
            .typeError(t('mustBeNumeric'))
            .required(t('required')),
        }),
      widthUnit: yup
        .object()
        .when('width', {
          is: (width: string) => Boolean(width),
          then: yup
            .object({
              label: yup.string(),
              value: yup.string().required(),
            })
            .typeError(t('required')),
        })
        .nullable(),
      height: yup
        .number()
        .transform((_value, originalValue) =>
          Number(originalValue.replace(/,/, '.'))
        )
        .typeError(t('mustBeNumeric'))
        .when('heightUnit', {
          is: (heightUnit: { label: string; value: string } | null) =>
            heightUnit?.value,
          then: yup
            .number()
            .transform((_value, originalValue) =>
              Number(originalValue.replace(/,/, '.'))
            )
            .typeError(t('mustBeNumeric'))
            .required(t('required')),
        }),
      heightUnit: yup
        .object()
        .when('height', {
          is: (height: string) => Boolean(height),
          then: yup
            .object({
              label: yup.string(),
              value: yup.string().required(),
            })
            .typeError(t('required')),
        })
        .nullable(),
      depth: yup
        .number()
        .transform((_value, originalValue) =>
          Number(originalValue.replace(/,/, '.'))
        )
        .typeError(t('mustBeNumeric'))
        .when('depthUnit', {
          is: (depthUnit: { label: string; value: string } | null) =>
            depthUnit?.value,
          then: yup
            .number()
            .transform((_value, originalValue) =>
              Number(originalValue.replace(/,/, '.'))
            )
            .typeError(t('mustBeNumeric'))
            .required(t('required')),
        }),
      depthUnit: yup
        .object()
        .when('depth', {
          is: (depth: string) => Boolean(depth),
          then: yup
            .object({
              label: yup.string(),
              value: yup.string().required(),
            })
            .typeError(t('required')),
        })
        .nullable(),
      grossWeight: yup
        .number()
        .transform((_value, originalValue) =>
          Number(originalValue.replace(/,/, '.'))
        )
        .typeError(t('mustBeNumeric'))
        .when('grossWeightUnit', {
          is: (grossWeightUnit: { label: string; value: string } | null) =>
            grossWeightUnit?.value,
          then: yup
            .number()
            .transform((_value, originalValue) =>
              Number(originalValue.replace(/,/, '.'))
            )
            .typeError(t('mustBeNumeric'))
            .required(t('required')),
        }),
      grossWeightUnit: yup
        .object()
        .when('grossWeight', {
          is: (grossWeight: string) => Boolean(grossWeight),
          then: yup
            .object({
              label: yup.string(),
              value: yup.string().required(),
            })
            .typeError(t('required')),
        })
        .nullable(),
    },
    [
      ['width', 'widthUnit'],
      ['height', 'heightUnit'],
      ['grossWeight', 'grossWeightUnit'],
      ['depth', 'depthUnit'],
    ]
  )

  const handleSubmit = async (values: Partial<FormikFieldType>) => {
    const masterProductCopy = { ...masterProduct }
    const temp = masterProductCopy.tradeItem.tradeItemInformation.extensions
    const { depth, height, width, packagingMaterial, grossWeight } = values
    const packagingTypeCode = values.packagingTypeCode?.value
    const depthUnit = values.depthUnit?.value
    const widthUnit = values.widthUnit?.value
    const heightUnit = values.heightUnit?.value
    const grossWeightUnit = values.grossWeightUnit?.value
    const depthObj = depthUnit
      ? {
          $: Number(depth?.replaceAll(',', '.')),
          '@measurementUnitCode': depthUnit,
        }
      : null
    const widthObj = widthUnit
      ? {
          $: Number(width?.replaceAll(',', '.')),
          '@measurementUnitCode': widthUnit,
        }
      : null
    const heightObj = heightUnit
      ? {
          $: Number(height?.replaceAll(',', '.')),
          '@measurementUnitCode': heightUnit,
        }
      : null
    const grossWeightObj = grossWeightUnit
      ? {
          $: Number(grossWeight?.replaceAll(',', '.')),
          '@measurementUnitCode': grossWeightUnit,
        }
      : null

    temp.packagingInformationModule = {
      packaging: [
        {
          ...temp.packagingInformationModule?.packaging?.[0],
          packagingTypeCode: packagingTypeCode || null,
          packagingMaterial: packagingMaterial || [],
        },
      ],
    }

    temp.tradeItemMeasurementsModule = {
      ...temp.tradeItemMeasurementsModule,
      tradeItemMeasurements: {
        ...temp.tradeItemMeasurementsModule?.tradeItemMeasurements,
        depth: depthObj,
        height: heightObj,
        width: widthObj,
        tradeItemWeight: {
          ...temp.tradeItemMeasurementsModule?.tradeItemMeasurements
            ?.tradeItemWeight,
          grossWeight: grossWeightObj,
        },
      },
    }

    await mutateAsync(masterProductCopy, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }
  const dimensions = ['width', 'height', 'depth']
  return (
    <Box maxWidth='500px' mt={9}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => handleSubmit(values)}>
        {({ setFieldValue, values, errors, dirty, isValid }) => (
          <Form style={{ height: '100%' }}>
            <Box mb={3}>
              <Autocomplete
                options={productPackagingOptions}
                onChange={(newUnit) =>
                  setFieldValue('packagingTypeCode', newUnit)
                }
                onClear={() => setFieldValue('packagingTypeCode', null)}
                value={
                  values['packagingTypeCode' as keyof FormikFieldType] || null
                }
                name='packagingTypeCode'
                label={t('packagingType')}
              />
            </Box>
            <PackagingMaterial materials={values.packagingMaterial} />
            <Typography variant='body2' mt={4} mb={4}>
              {t('dimensions')}
            </Typography>
            {dimensions.map((dimension) => (
              <Box
                key={dimension}
                display={isMobile ? 'block' : 'flex'}
                mt={isMobile ? theme.spacing(3) : 0}>
                <Input
                  width='48%'
                  errorMessage={
                    errors[dimension as keyof FormikFieldType] as
                      | string
                      | undefined
                  }
                  label={t(dimension)}
                  name={dimension}
                  type='text'
                  variant='filled'
                  dependentValidation
                />
                <Autocomplete
                  options={measurementUnitOptions ?? []}
                  onChange={(newUnit) =>
                    setFieldValue(`${dimension}Unit`, newUnit)
                  }
                  onClear={() => setFieldValue(`${dimension}Unit`, null)}
                  value={
                    values[`${dimension}Unit` as keyof FormikFieldType] || null
                  }
                  name={`${dimension}Unit`}
                  label={t(`${dimension}Unit`)}
                  ml={theme.spacing(2)}
                  error={
                    errors[`${dimension}Unit` as keyof FormikFieldType] as
                      | string
                      | undefined
                  }
                />
              </Box>
            ))}
            <Typography variant='body2' mb={4}>
              {t('weight')}
            </Typography>
            <Box display={isMobile ? 'block' : 'flex'} mb={7}>
              <Input
                width='48%'
                errorMessage={
                  errors['grossWeight' as keyof FormikFieldType] as
                    | string
                    | undefined
                }
                label={t('grossWeight')}
                name='grossWeight'
                type='text'
                variant='filled'
                dependentValidation
              />
              <Autocomplete
                options={measurementUnitOptions ?? []}
                onChange={(newUnit) =>
                  setFieldValue('grossWeightUnit', newUnit)
                }
                onClear={() => setFieldValue('grossWeightUnit', null)}
                value={
                  values['grossWeightUnit' as keyof FormikFieldType] || null
                }
                name='grossWeightUnit'
                label={t('grossWeightUnit')}
                ml={theme.spacing(2)}
                error={
                  errors['grossWeightUnit' as keyof FormikFieldType] as
                    | string
                    | undefined
                }
              />
            </Box>
            <Box
              mt={3}
              sx={{
                position: 'fixed',
                bottom: isMobile ? '0px' : theme.spacing(2),
                width: isMobile ? '92%' : '504px',
                zIndex: 1000,
              }}>
              <SaveBtn
                isValid={isValid}
                dirty={dirty}
                showOverlay
                isLoadingRequest={isLoadingPutRequest}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
